import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, CameraViewer, FileDropZone, FlowLayout, Skeleton } from '@/ds'
import { useMediaQuery } from '@/app/stay/useMediaQuery'
import { useAppNavigate } from '@/lib/query-params'
import { checkinService } from '@/services/checkin'
import { CaptureError, readQr } from '@/services/checkin/capture'
import type { CaptureErrorCode, DocumentReading, MiDniExtractedData, ReadContext, Side } from '@/services/checkin/capture'
import { SdkSlot, useCheckinFlow } from '../CheckinFlow'
import { backLabelKey, neighbours, progressFor, stepPath } from '../flow'
import { CaptureErrorState } from '../captureErrors'

type Status = { kind: 'starting' } | { kind: 'ready'; side: Side } | { kind: 'capturing'; side: Side } | { kind: 'captured'; side: Side; image: Blob } | { kind: 'reading' } | { kind: 'qr' } | { kind: 'error'; code: CaptureErrorCode }

const LEGACY = 'v2.legacy.stepper'

function usePreviewUrl(image: Blob | null) {
  // JSDOM no implementa esta API; en navegadores la foto siempre llega como Blob desde MobbScan.
  const url = useMemo(() => (image && typeof URL.createObjectURL === 'function' ? URL.createObjectURL(image) : null), [image])
  useEffect(() => {
    return () => {
      if (url) URL.revokeObjectURL?.(url)
    }
  }, [url])
  return url
}

/**
 * Paso 3 · Escaneo del documento. País y tipo se confirmaron en el paso anterior antes de iniciar MobbScan.
 */
export function Documento() {
  const { t } = useTranslation()
  const { go } = useAppNavigate()
  const desktop = useMediaQuery('(min-width: 1024px)')
  const { stay, draft, update, updateGuest, setVolatile, capture, qr } = useCheckinFlow()
  const [attempt, setAttempt] = useState(0)
  const videoRef = useRef<HTMLVideoElement>(null)
  const { prev } = neighbours('3', stay, draft.persona, draft.method, draft.entry)

  const countries = stay.registration.catalogs.documentCountries
  const country = countries.find((c) => c.id === draft.docCountryId) ?? countries[0]
  const docType = country?.documentTypes.find((d) => d.id === draft.documentTypeId) ?? country?.documentTypes[0]
  const isQr = docType?.scanType === 'qr'
  const forceScan = stay.registration.rules.forceScan
  // Estado de la captura ligado al documento y al intento: al cambiar, vuelve a "arrancando" sin setState en el efecto.
  const token = `${docType?.id ?? ''}#${attempt}`
  const [captureState, setCaptureState] = useState<{ token: string; status: Status } | null>(null)
  const status: Status = captureState?.token === token ? captureState.status : isQr ? { kind: 'qr' } : { kind: 'starting' }
  const setStatus = useCallback((next: Status) => setCaptureState({ token, status: next }), [token])

  const context = useCallback(
    (): ReadContext | null =>
      docType
        ? {
            stay,
            documentType: docType,
            current: { ...draft.guest, countryId: draft.guest.countryId || (country?.id ?? '') },
            getMunicipalities: (provinceId) => checkinService.getMunicipalities(stay, provinceId),
            persona: draft.persona,
          }
        : null,
    [stay, docType, draft.guest, draft.persona, country],
  )

  const finish = useCallback(
    (reading: DocumentReading, method: 'scan' | 'qr') => {
      updateGuest(reading.guest)
      update({ doubtful: reading.doubtful, method })
      setVolatile({ attachments: reading.attachments, rawData: reading.rawData })
      go(stepPath('3b'))
    },
    [go, update, updateGuest, setVolatile],
  )

  const readCaptured = useCallback(() => {
    const readCtx = context()
    if (!readCtx) return
    setStatus({ kind: 'reading' })
    capture
      .read(readCtx)
      .then((result) => finish(result, 'scan'))
      .catch((err: unknown) => setStatus({ kind: 'error', code: err instanceof CaptureError ? err.code : 'unreadable' }))
  }, [capture, context, finish, setStatus])

  // Arranca MobbScan (o MiDNI) al entrar y al cambiar de documento.
  useEffect(() => {
    if (!docType) return
    const ctx = context()
    if (isQr) {
      let stop: (() => void) | null = null
      let cancelled = false
      const video = videoRef.current ?? document.createElement('video')
      void qr
        .start(
          video,
          stay.id,
          (data: MiDniExtractedData) => {
            if (!ctx) return
            setStatus({ kind: 'reading' })
            readQr(data, ctx)
              .then((r) => !cancelled && finish(r, 'qr'))
              .catch(() => !cancelled && setStatus({ kind: 'error', code: 'unreadable' }))
          },
          (code) => !cancelled && setStatus({ kind: 'error', code }),
        )
        .then((s) => {
          stop = s
          if (cancelled) s()
        })
      return () => {
        cancelled = true
        stop?.()
      }
    }
    capture.startDocument({ containerId: 'mobbscan-container', documentType: docType }, (e) => {
      if (e.type === 'ready') setStatus({ kind: 'ready', side: capture.missingSides()[0] ?? 'front' })
      else if (e.type === 'capturing') setStatus({ kind: 'capturing', side: e.side })
      else if (e.type === 'error') setStatus({ kind: 'error', code: e.code })
      else if (e.type === 'captured') {
        setStatus({ kind: 'captured', side: e.side, image: e.image })
      }
    })
    // Solo al cambiar de documento o reintentar: el contexto se lee en el momento de la lectura.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docType?.id, isQr, attempt])

  const manual = forceScan
    ? null
    : () => {
        update({ method: 'manual', doubtful: [] })
        setVolatile({ attachments: {}, rawData: undefined })
        go(stepPath('3b'))
      }

  const side = status.kind === 'ready' || status.kind === 'capturing' ? status.side : 'front'
  const hint = isQr ? t(`${LEGACY}.collectDataForm.miDniQr.instructions`) : side === 'back' ? t('v2.checkin.documento.otraCara') : t('v2.checkin.documento.consejo')
  const busy = status.kind === 'capturing' || status.kind === 'starting'
  const showPrimary = !desktop && !isQr && (status.kind === 'ready' || status.kind === 'capturing' || status.kind === 'starting')
  const capturedSide = status.kind === 'captured' ? status.side : null
  const previewUrl = usePreviewUrl(status.kind === 'captured' ? status.image : null)
  const nextSide = status.kind === 'captured' ? capture.missingSides()[0] : null
  const confirmCapture = () => {
    if (nextSide) {
      capture.showSide(nextSide)
      setStatus({ kind: 'ready', side: nextSide })
      return
    }
    readCaptured()
  }

  return (
    <FlowLayout
      progress={{ ...progressFor('3', stay, draft.persona, draft.method, draft.entry), backLabel: t(backLabelKey(prev)), onBack: () => go(stepPath(prev)) }}
      title={t('v2.checkin.documento.titulo')}
      intro={desktop ? t('v2.checkin.documento.introEscritorio') : t('v2.checkin.documento.intro')}
      primary={
        status.kind === 'captured'
          ? { label: nextSide ? t('v2.checkin.documento.continuarOtraCara') : t('v2.checkin.documento.revisarDatos'), onClick: confirmCapture }
          : showPrimary
            ? { label: t('v2.checkin.documento.hacerFoto'), icon: 'camera', loading: busy, onClick: () => capture.capture(side) }
            : undefined
      }
    >
      {status.kind === 'error' ? (
        <CaptureErrorState code={status.code} qr={isQr} onRetry={() => setAttempt((a) => a + 1)} onManual={manual} />
      ) : status.kind === 'reading' ? (
        <div className="checkin__processing" role="status" aria-busy="true">
          <Skeleton height={220} radius={16} />
          <p className="checkin__processing-title">{t(`${LEGACY}.autoCollect.processingDoc.title`)}</p>
          <p className="checkin__note">{t(`${LEGACY}.autoCollect.processingDoc.description`)}</p>
        </div>
      ) : status.kind === 'captured' ? (
        <div className="checkin__capture-review">
          <p className="checkin__capture-review-title">{t('v2.checkin.documento.fotoCapturada', { side: capturedSide === 'back' ? t('v2.checkin.documento.reverso') : t('v2.checkin.documento.anverso') })}</p>
          {previewUrl && <img className="checkin__capture-preview" src={previewUrl} alt={t('v2.checkin.documento.previsualizacion', { side: capturedSide === 'back' ? t('v2.checkin.documento.reverso') : t('v2.checkin.documento.anverso') })} />}
          <p className="checkin__note">{t('v2.checkin.documento.revisarFoto')}</p>
          <Button variant="secondary" onClick={() => capturedSide && capture.capture(capturedSide)}>
            {t('v2.checkin.documento.repetirFoto')}
          </Button>
        </div>
      ) : isQr ? (
        <CameraViewer guide="document" hint={hint}>
          <video ref={videoRef} className="checkin__video" muted playsInline aria-hidden="true" />
        </CameraViewer>
      ) : desktop ? (
        <>
          <FileDropZone
            prompt={t('v2.checkin.documento.arrastra')}
            buttonLabel={t('v2.checkin.documento.elegir')}
            hint={t('v2.checkin.documento.formatos')}
            accept="image/jpeg,image/png,application/pdf"
            onChoose={() => capture.pickFile(side)}
            onFiles={(files) => files[0] && capture.giveFile(side, files[0])}
          />
          <div className="checkin__sdk-hidden">
            <SdkSlot />
          </div>
        </>
      ) : (
        <CameraViewer guide="document" className="checkin__document-camera" hint={<span aria-live="polite">{hint}</span>}>
          <SdkSlot mode="document" />
        </CameraViewer>
      )}

      {status.kind !== 'error' && !isQr && !desktop && (
        <div className="checkin__two">
          <Button variant="secondary" onClick={() => capture.pickFile(side)} disabled={status.kind === 'reading'}>
            {t('v2.checkin.documento.subirFoto')}
          </Button>
          {manual && (
            <Button variant="secondary" onClick={manual} disabled={status.kind === 'reading'}>
              {t('v2.checkin.documento.aMano')}
            </Button>
          )}
        </div>
      )}
      {status.kind !== 'error' && (isQr || desktop) && manual && (
        <Button variant="tertiary" onClick={manual}>
          {t('v2.checkin.documento.aMano')}
        </Button>
      )}
      <p className="checkin__note">{desktop ? t('v2.checkin.documento.privacidadEscritorio') : t('v2.checkin.documento.privacidad')}</p>
    </FlowLayout>
  )
}
